import { PlainNotice } from '@/components/plain-notice';
import { CONTENT_ONLY } from '@/lib/ui-mode';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { CheckIcon, LockIcon } from 'lucide-react';
import { AuthError } from '@/components/auth/auth-error';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ThemeToggle } from '@/components/theme-toggle';
import { messageForError } from '@/lib/api-client';
import { authApi } from '@/lib/api/auth';
import { organisationsApi } from '@/lib/api/organisations';
import { getAuthState, setLastOrgId, setProfile } from '@/lib/auth-store';
import { cn } from '@/lib/utils';

const schema = z.object({
  name: z.string().min(1, 'Give your organisation a name').max(200),
});
type Values = z.infer<typeof schema>;

/** Steps 2-9 are stubbed until the milestones that own them land. */
const LATER_STEPS = [
  { label: 'Invite your team', detail: 'Add editors and publishers, and set their roles.' },
  { label: 'Connect your first site', detail: 'Point SiteFlow at a site you already run.' },
  { label: 'Verify the domain', detail: 'Prove ownership by DNS TXT record or an HTML file.' },
  { label: 'Activate a licence', detail: 'Bind a production slot to the verified domain.' },
  { label: 'Import existing content', detail: 'Pull the current copy in as the first version.' },
  { label: 'Define your content model', detail: 'Decide which regions editors may change.' },
  { label: 'Publish a first change', detail: 'Push an edit through the full publish pipeline.' },
  { label: 'Set up billing', detail: 'Choose a plan and add a payment method.' },
] as const;

function OnboardingPage() {
  const navigate = useNavigate();

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { name: '' },
  });

  const createMutation = useMutation({
    mutationFn: (values: Values) => organisationsApi.create({ name: values.name }),
    onSuccess: async (organisation) => {
      // Re-read the profile so the new membership and its permissions are live.
      try {
        const me = await authApi.me();
        setProfile(me.user, me.organisations);
      } catch {
        /* the redirect guard will recover on the next navigation */
      }
      setLastOrgId(organisation.id);
      toast.success(`${organisation.name} is ready`);
      void navigate({ to: '/app/$orgId', params: { orgId: organisation.id } });
    },
  });

  const pending = createMutation.isPending;

  return (
    <div className="min-h-dvh bg-surface">
      <header className="flex h-14 items-center gap-2 border-b border-hairline bg-elevated px-6">
        <span className="flex size-6 items-center justify-center rounded-sm bg-ink text-micro font-bold text-ink-inverse">
          SF
        </span>
        <span className="text-data font-semibold tracking-tight text-ink">SiteFlow CMS</span>
        <div className="ml-auto">
          <ThemeToggle />
        </div>
      </header>

      <main className="mx-auto flex max-w-3xl flex-col gap-8 px-6 py-12">
        <header className="flex flex-col gap-2">
          <span className="section-label">Step 1 of 9</span>
          <h1 className="text-display font-semibold tracking-tight text-ink">
            Name your organisation
          </h1>
          <p className="max-w-prose text-body leading-relaxed text-ink-muted">
            An organisation holds your sites, your team and your licence. Most companies need
            exactly one — use separate organisations only when billing or access must be fully
            isolated.
          </p>
        </header>

        <Card>
          <CardHeader>
            <CardTitle>Organisation details</CardTitle>
            <CardDescription>You can rename it later from Settings.</CardDescription>
          </CardHeader>
          <Form {...form}>
            <form
              noValidate
              onSubmit={form.handleSubmit((values) => createMutation.mutate(values))}
            >
              <CardContent className="flex flex-col gap-4">
                <AuthError
                  message={
                    createMutation.isError
                      ? messageForError(
                          createMutation.error,
                          'We could not create that organisation.',
                        )
                      : null
                  }
                />

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Organisation name</FormLabel>
                      <FormControl>
                        <Input placeholder="Northwind Studios" disabled={pending} {...field} />
                      </FormControl>
                      <FormDescription>
                        This is what your team sees in the organisation switcher.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" size="lg" disabled={pending} className="self-start">
                  {pending ? 'Creating…' : 'Create organisation'}
                </Button>
              </CardContent>
            </form>
          </Form>
        </Card>

        <section className="flex flex-col gap-3">
          <h2 className="section-label">What comes next</h2>
          <ol className="overflow-hidden rounded-lg border border-hairline bg-elevated">
            <li className="flex items-center gap-3 border-b border-hairline px-5 py-3">
              <span className="flex size-5 shrink-0 items-center justify-center rounded-full border border-accent bg-accent text-accent-ink">
                <CheckIcon className="size-3" />
              </span>
              <span className="flex-1 text-data font-medium text-ink">Create your organisation</span>
              <span className="font-mono text-micro text-ink-subtle" data-numeric>
                01
              </span>
            </li>

            {LATER_STEPS.map((step, index) => (
              <li
                key={step.label}
                className={cn(
                  'flex items-center gap-3 border-b border-hairline px-5 py-3 last:border-b-0',
                  'opacity-50',
                )}
                aria-disabled="true"
              >
                <span className="flex size-5 shrink-0 items-center justify-center rounded-full border border-hairline-strong text-ink-subtle">
                  <LockIcon className="size-2.5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-data text-ink">{step.label}</span>
                  <span className="block text-micro text-ink-muted">{step.detail}</span>
                </span>
                <span className="font-mono text-micro text-ink-subtle" data-numeric>
                  {String(index + 2).padStart(2, '0')}
                </span>
              </li>
            ))}
          </ol>
          <p className="text-micro text-ink-subtle">
            Later steps unlock as the sites, content, licensing and billing milestones ship.
          </p>
        </section>
      </main>
    </div>
  );
}

function NoAccess() {
  return (
    <PlainNotice title="No access yet" fullPage>
      <p>
        This account has not been given access to the website. Please ask your administrator to
        add you.
      </p>
    </PlainNotice>
  );
}

export const Route = createFileRoute('/onboarding')({
  beforeLoad: ({ location }) => {
    if (getAuthState().status !== 'authenticated') {
      throw redirect({
        to: '/login',
        search: { redirect: `${location.pathname}${location.searchStr}` },
      });
    }
  },
  component: CONTENT_ONLY ? NoAccess : OnboardingPage,
});
